import { useState, useEffect, type ReactNode, useCallback } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Shield, ShoppingCart, Package, UserRound } from 'lucide-react'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'
import { getPublicBanner } from '#bv/lib/adminApi'
import type { BannerItem } from '#bv/lib/adminTypes'
import { useCart } from '#bv/lib/cart'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { tryGetSupabaseClient } from '#bv/lib/supabaseClient'
import { CurrencySelector } from '#bv/components/CurrencySelector'
import { navigateToShopSearch } from '#bv/lib/shopSearch'

const FALLBACK_MARQUEE: BannerItem[] = [
  { icon: 'zap', text: 'INSTANT DELIVERY ON ALL ORDERS' },
  { icon: 'lock', text: 'SECURE CHECKOUT — SSL ENCRYPTED' },
  { icon: 'gamepad', text: 'FORTNITE & CLASH ACCOUNTS — FRESH STOCK DAILY' },
  { icon: 'gem', text: 'USE CODE PULSE10 FOR 10% OFF FIRST ORDER' },
  { icon: 'check', text: 'COMMUNITY ON DISCORD' },
  { icon: 'rocket', text: 'DISCORD BOOST SALE — UP TO 40% OFF' },
]

function MarqueeLine({ text }: { text: string }): ReactNode {
  const marker = 'PULSE10'
  const idx = text.indexOf(marker)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <strong className="text-[#00bfff]">{marker}</strong>
      {text.slice(idx + marker.length)}
    </>
  )
}

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [banner, setBanner] = useState<{ enabled: boolean; items: BannerItem[] } | null>(null)
  const [bannerReady, setBannerReady] = useState(false)
  const [customerEmail, setCustomerEmail] = useState<string | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const navigate = useNavigate()
  const { count } = useCart()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    getPublicBanner()
      .then((value) => setBanner(value))
      .catch(() => setBanner({ enabled: true, items: FALLBACK_MARQUEE }))
      .finally(() => setBannerReady(true))
  }, [])

  useEffect(() => {
    const supabase = tryGetSupabaseClient()
    if (!supabase) {
      setCustomerEmail(null)
      return
    }
    let cancelled = false

    void (async () => {
      try {
        const { data } = await supabase.auth.getSession()
        const em = data.session?.user?.email ?? null
        if (!cancelled) setCustomerEmail(em)
      } catch {
        if (!cancelled) setCustomerEmail(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const runSearch = useCallback(
    (raw?: string) => {
      const q = (raw ?? searchQuery).trim()
      if (!q) return
      setMoreOpen(false)
      navigateToShopSearch(navigate, q)
      setSearchQuery('')
    },
    [navigate, searchQuery],
  )

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const raw = new FormData(e.currentTarget).get('q')
    runSearch(typeof raw === 'string' ? raw : undefined)
  }

  const primaryLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/donation', label: 'Donation' },
    { to: '/contact', label: 'Contact' },
  ]

  const moreLinks = [
    { to: '/faq', label: 'FAQ' },
    { to: '/terms', label: 'Terms' },
    { to: '/refund', label: 'Refunds' },
  ]

  const showMarquee = bannerReady && banner?.enabled !== false
  const rows = banner && banner.items.length > 0 ? banner.items : FALLBACK_MARQUEE

  return (
    <>
      {showMarquee ? (
        <div
          className="relative overflow-hidden border-b"
          style={{
            background: 'linear-gradient(90deg, #08060a 0%, #140818 50%, #08060a 100%)',
            borderColor: 'rgba(0, 191, 255, 0.26)',
            height: '32px',
          }}
        >
          <div className="flex h-full items-center">
            <div className="marquee-inner flex items-center gap-16 whitespace-nowrap px-8" style={{ width: 'max-content' }}>
              {[...Array(2)].map((_, i) => (
                <span key={i} className="flex items-center gap-16 text-xs font-semibold" style={{ color: '#f0f6ff' }}>
                  {rows.map((row, j) => (
                    <span key={`${row.icon}-${j}-${i}`} className="inline-flex items-center gap-2">
                      <FeatureIcon name={row.icon as FeatureIconName} size={16} className="shrink-0" />
                      <span>
                        <MarqueeLine text={row.text} />
                      </span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <header
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(7,5,8,0.97)' : 'rgba(7,5,8,0.88)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${scrolled ? 'rgba(123,47,190,0.14)' : 'rgba(0,191,255,0.08)'}`,
          boxShadow: scrolled ? '0 4px 40px rgba(0,0,0,0.6)' : 'none',
        }}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-3 sm:h-16 sm:gap-4 sm:px-6">
          <Link to="/" className="group flex shrink-0 items-center gap-3">
            <img
              src="/pulsedigital-logo-mark.png"
              alt="PulseDigital"
              className="h-9 w-auto shrink-0 transition-transform duration-300 group-hover:scale-110 sm:h-10"
              style={{ filter: 'drop-shadow(0 0 8px rgba(0, 191, 255,0.35)) drop-shadow(0 0 12px rgba(123, 47, 190,0.25))' }}
            />
            <span
              className="font-display text-xl tracking-wide max-[380px]:hidden sm:text-2xl sm:tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #c4d9ff 32%, #00bfff 58%, #93c5fd 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              PulseDigital
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {primaryLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-lg px-3 py-2 text-sm font-medium text-[#a8c4e8] transition-all duration-200 hover:bg-white/[0.04] hover:text-[#f0f6ff]"
                activeProps={{
                  className:
                    'rounded-lg px-3 py-2 text-sm font-medium bg-white/[0.04] text-[#f0f6ff] ring-1 ring-white/[0.08]',
                }}
                activeOptions={{ exact: true }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <CurrencySelector className="inline-flex max-[360px]:hidden" />

            <Link
              to="/cart"
              className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#a8c4e8] transition-all duration-200 hover:bg-white/[0.04] hover:text-[#f0f6ff] hover:ring-1 hover:ring-white/[0.08]"
              title="Cart"
              aria-label="Cart"
            >
              <ShoppingCart className="h-[18px] w-[18px]" strokeWidth={2} />
              {count > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-4 rounded-full bg-[#00bfff] px-1 text-[10px] leading-4 font-bold text-white">
                  {count}
                </span>
              ) : null}
            </Link>

            <Link
              to="/shop"
              className="btn-primary h-9 w-9 shrink-0 rounded-lg md:h-auto md:w-auto md:px-3 md:py-2"
              aria-label="Shop"
              title="Shop"
            >
              <Package className="h-[16px] w-[16px] md:mr-2" strokeWidth={2} />
              <span className="hidden md:inline text-sm font-semibold">Shop</span>
            </Link>

            <Link
              to={customerEmail ? '/customer' : '/customer/login'}
              search={customerEmail ? undefined : { next: '/customer' }}
              className="btn-chrome h-9 w-9 shrink-0 rounded-lg md:h-auto md:w-auto md:px-3 md:py-2"
              title={customerEmail ? 'Dashboard' : 'Sign in'}
              aria-label={customerEmail ? 'Customer dashboard' : 'Sign in'}
            >
              <UserRound className="h-[16px] w-[16px] md:mr-2" strokeWidth={2.2} />
              <span className="hidden whitespace-nowrap text-sm font-semibold md:inline">
                {customerEmail ? 'Dashboard' : 'Sign in'}
              </span>
            </Link>

            {/* More menu (search / discord / admin) */}
            <div className="relative hidden md:block">
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a8c4e8] transition-all duration-200 hover:bg-white/[0.04] hover:text-[#f0f6ff] hover:ring-1 hover:ring-white/[0.08]"
                aria-label="More"
                title="More"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                </svg>
              </button>
              {moreOpen ? (
                <div
                  className="absolute right-0 mt-2 w-72 rounded-2xl border border-white/10 bg-black/80 p-3 shadow-2xl"
                  style={{ backdropFilter: 'blur(18px)' }}
                >
                  <form onSubmit={handleSearch} className="flex items-center gap-2">
                    <input
                      type="text"
                      name="q"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products…"
                      className="bv-input w-full rounded-xl px-3 py-2 text-sm"
                    />
                    <button type="submit" className="btn-chrome rounded-xl px-3 py-2 text-xs font-semibold">
                      Go
                    </button>
                  </form>
                  <div className="mt-3 space-y-1">
                    {moreLinks.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        className="flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#f0f6ff] hover:bg-white/5"
                        onClick={() => setMoreOpen(false)}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-[#00bfff] opacity-70" aria-hidden />
                          {l.label}
                        </span>
                        <span className="text-xs text-[#5a6d8a]">Info</span>
                      </Link>
                    ))}
                    <a
                      href="https://discord.gg/tGxhDfRkdE"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#f0f6ff] hover:bg-white/5"
                    >
                      <span className="inline-flex items-center gap-2">
                        <DiscordIcon size={16} white className="shrink-0" />
                        Discord
                      </span>
                      <span className="text-xs text-[#5a6d8a]">Support</span>
                    </a>
                    <Link
                      to="/admin/login"
                      className="flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#f0f6ff] hover:bg-white/5"
                      onClick={() => setMoreOpen(false)}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Shield className="h-4 w-4" strokeWidth={2} />
                        Admin
                      </span>
                      <span className="text-xs text-[#5a6d8a]">Login</span>
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[#8ecfff] transition-all hover:bg-white/5 hover:text-[#f0f6ff] md:hidden"
              aria-label="Menu"
            >
              {mobileOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <div
            className="border-t md:hidden"
            style={{ borderColor: 'rgba(0,191,255,0.08)', background: 'rgba(8,10,18,0.98)', backdropFilter: 'blur(20px)' }}
          >
            <nav className="flex flex-col gap-1 px-4 py-4">
              {primaryLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-xl px-4 py-3 text-base font-medium text-[#8ecfff] transition-all hover:bg-white/5 hover:text-[#f0f6ff]"
                  activeProps={{ className: 'rounded-xl px-4 py-3 text-base font-medium bg-white/8 text-[#f0f6ff]' }}
                >
                  {link.label}
                </Link>
              ))}
              {moreLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-xl px-4 py-3 text-base font-medium text-[#8ecfff] transition-all hover:bg-white/5 hover:text-[#f0f6ff]"
                  activeProps={{ className: 'rounded-xl px-4 py-3 text-base font-medium bg-white/8 text-[#f0f6ff]' }}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4 py-2 sm:hidden">
                <CurrencySelector />
              </div>
              <Link
                to="/cart"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between rounded-xl px-4 py-3 text-base font-medium text-[#8ecfff] hover:bg-white/5 hover:text-[#f0f6ff]"
              >
                <span className="inline-flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 shrink-0" strokeWidth={2} />
                  Cart
                </span>
                {count > 0 ? <span className="rounded-full bg-[#00bfff] px-2 py-0.5 text-xs text-white">{count}</span> : null}
              </Link>
              <Link
                to={customerEmail ? '/customer' : '/customer/login'}
                search={customerEmail ? undefined : { next: '/customer' }}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-semibold text-[#f0f6ff] hover:bg-white/5"
                style={{ background: 'rgba(0, 191, 255,0.12)', border: '1px solid rgba(0, 191, 255,0.2)' }}
                title={customerEmail ? 'Dashboard' : 'Login'}
                aria-label="Dashboard / Login"
              >
                <UserRound className="h-5 w-5 shrink-0" strokeWidth={2.2} />
                <span className="whitespace-nowrap">{customerEmail ? 'Dashboard' : 'Sign in'}</span>
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-[#8ecfff] hover:bg-white/5 hover:text-[#93c5fd]"
                title="Admin login"
                aria-label="Admin login"
              >
                <Shield className="h-5 w-5 shrink-0" strokeWidth={2} />
                <span>Admin</span>
              </Link>
              <div className="mt-2 border-t pt-3 pb-1" style={{ borderColor: 'rgba(0,191,255,0.06)' }}>
                <form
                  onSubmit={(e) => {
                    handleSearch(e)
                    setMobileOpen(false)
                  }}
                  className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-2"
                >
                  <input
                    type="text"
                    name="q"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products…"
                    className="w-full bg-transparent px-2 py-2 text-sm text-[#f0f6ff] placeholder:text-[#5a6d8a] outline-none"
                  />
                  <button type="submit" className="btn-chrome shrink-0 rounded-xl px-3 py-2 text-xs font-semibold">
                    Go
                  </button>
                </form>
                <a
                  href="https://discord.gg/tGxhDfRkdE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-discord flex rounded-xl px-4 py-3 text-base"
                  aria-label="Discord"
                  title="Discord"
                >
                  <DiscordIcon size={18} white className="shrink-0" />
                </a>
              </div>
            </nav>
          </div>
        ) : null}
      </header>
    </>
  )
}
